import { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Star, Quote, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";

const testimonials = [
  {
    name: "Sarah Jenkins",
    role: "CEO, TechVentures Inc.",
    avatar:
      "https://images.pexels.com/photos/33871730/pexels-photo-33871730.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200",
    quote:
      "Softlume transformed our digital presence entirely. Their team delivered a platform that exceeded our expectations in both design and functionality. The attention to detail was remarkable, and they were incredibly responsive to our feedback throughout the entire process.",
    rating: 5,
    project: "Enterprise Dashboard",
  },
  {
    name: "Marcus Thorne",
    role: "CTO, DataFlow Systems",
    avatar:
      "https://images.pexels.com/photos/31307734/pexels-photo-31307734.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200",
    quote:
      "Working with Softlume was a game-changer for our product. Their engineering team is top-notch and they truly understand the nuances of building scalable enterprise software. They reduced our time-to-market by 40%.",
    rating: 5,
    project: "Data Analytics Platform",
  },
  {
    name: "Elena Vasquez",
    role: "Product Director, InnoHealth",
    avatar:
      "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&crop=faces",
    quote:
      "From concept to deployment, Softlume guided us through every step. The mobile app they built has been instrumental in growing our user base by 300% in just six months. Their UX expertise is unmatched.",
    rating: 5,
    project: "Healthcare Mobile App",
  },
  {
    name: "David Chen",
    role: "Founder, LogiStar",
    avatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=faces",
    quote:
      "The custom software solution Softlume developed streamlined our entire operation. Their team's technical prowess and communication were exceptional throughout the project. We've seen a 25% increase in efficiency.",
    rating: 5,
    project: "Logistics Management System",
  },
  {
    name: "Amanda Wright",
    role: "VP Marketing, GrowthCo",
    avatar:
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&h=200&fit=crop&crop=faces",
    quote:
      "Softlume built us a marketing automation platform that has completely changed how we operate. The ROI has been incredible - we saw a 200% improvement in campaign performance within the first quarter.",
    rating: 5,
    project: "Marketing Automation Tool",
  },
  {
    name: "Robert Kim",
    role: "COO, FinEdge Capital",
    avatar:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop&crop=faces",
    quote:
      "Security was our top priority, and Softlume delivered a financial platform that meets the highest industry standards. Their knowledge of compliance requirements saved us months of back-and-forth with regulators.",
    rating: 5,
    project: "Financial Trading Platform",
  },
  {
    name: "Lisa Chang",
    role: "Director of Engineering, EduTech",
    avatar:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=faces",
    quote:
      "We needed an e-learning platform that could handle 100K+ concurrent users. Softlume architected a solution that not only met but exceeded our scalability requirements. The platform hasn't had a single minute of downtime.",
    rating: 5,
    project: "E-Learning Platform",
  },
  {
    name: "Michael Torres",
    role: "CEO, RetailNext",
    avatar:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=faces",
    quote:
      "Softlume's e-commerce solution helped us pivot to online sales during a critical period. Their speed of delivery and quality of work were outstanding. Our online revenue grew 5x in the first year.",
    rating: 5,
    project: "E-Commerce Platform",
  },
];

export default function TestimonialsPage() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="testimonials-page"
    >
      {/* Hero */}
      <section
        ref={heroRef}
        className="relative min-h-[50vh] flex items-center overflow-hidden pt-20"
      >
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/95 to-slate-950" />
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-cyan-500/5 rounded-full blur-[120px]" />
        </motion.div>
        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative max-w-7xl mx-auto px-6 md:px-12 py-20"
        >
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-sm font-medium text-blue-400 tracking-widest uppercase mb-4 block"
          >
            Testimonials
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight text-white mb-6"
          >
            Client <span className="glow-text">Stories</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed"
          >
            Real feedback from real clients. Discover how we've helped
            businesses across industries achieve their digital goals.
          </motion.p>
        </motion.div>
      </section>

      {/* Testimonials Grid */}
      <section className="relative py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <TestimonialCard key={t.name} testimonial={t} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <StatsBar />

      {/* CTA */}
      <section className="relative py-24 bg-slate-950">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
        <div className="relative max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-white mb-6">
            Ready to Be Our Next Success Story?
          </h2>
          <p className="text-base text-slate-400 mb-8">
            Join the growing list of satisfied clients who trust Softlume with
            their digital transformation.
          </p>
          <Button
            data-testid="testimonials-page-cta"
            onClick={() => navigate("/contact")}
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-8 h-12 text-base font-semibold hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] group"
          >
            Start Your Project{" "}
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>
    </motion.div>
  );
}

function TestimonialCard({ testimonial: t, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      whileHover={{ y: -4 }}
      data-testid={`testimonials-page-card-${index}`}
      className="p-6 md:p-8 rounded-2xl border border-white/5 bg-slate-900/30 hover:border-blue-500/20 transition-all duration-300 group"
    >
      <Quote className="w-10 h-10 text-blue-500/15 mb-4 group-hover:text-blue-500/25 transition-colors" />
      <p className="text-base text-slate-300 leading-relaxed mb-6">
        "{t.quote}"
      </p>
      <div className="flex items-center gap-4">
        <img
          src={t.avatar}
          alt={t.name}
          className="w-12 h-12 rounded-full object-cover border-2 border-white/10 group-hover:border-blue-500/30 transition-colors"
        />
        <div className="flex-1">
          <div className="text-sm font-medium text-white">{t.name}</div>
          <div className="text-xs text-slate-500">{t.role}</div>
          {t.project && (
            <div className="text-xs text-blue-400/60 mt-0.5">
              Project: {t.project}
            </div>
          )}
        </div>
        <div className="flex gap-0.5">
          {Array.from({ length: t.rating }).map((_, i) => (
            <Star key={i} className="w-4 h-4 fill-yellow-500 text-yellow-500" />
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function StatsBar() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const stats = [
    { value: "99%", label: "Client Satisfaction" },
    { value: "200+", label: "Projects Completed" },
    { value: "4.9/5", label: "Average Rating" },
    { value: "95%", label: "Repeat Clients" },
  ];
  return (
    <section className="relative py-16 bg-[#0a0f1e]">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
      <div ref={ref} className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1 }}
              className="text-center"
            >
              <div className="text-3xl md:text-4xl font-heading font-bold text-white">
                {s.value}
              </div>
              <div className="text-sm text-slate-500 mt-1">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
